import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Launch as LaunchIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMicroSites } from '../../store/actions/microSiteActions';
import CreateMicroSite from './CreateMicroSite';
import EditMicroSite from './EditMicroSite';
import { useNavigate } from 'react-router-dom';

function MicroSiteList({ accountId }) {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedSite, setSelectedSite] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const { microSites, isLoading, error } = useSelector((state) => state.microSite);
  const navigate = useNavigate();

  useEffect(() => {
    if (accountId) {
      dispatch(fetchMicroSites(accountId));
    }
  }, [accountId, dispatch]);

  const handleMenuOpen = (event, site) => {
    setAnchorEl(event.currentTarget);
    setSelectedSite(site);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = (site) => {
    setSelectedSite(site);
    setIsEditOpen(true);
  };

  const handleEditClose = () => {
    setIsEditOpen(false);
    setSelectedSite(null);
  };

  const handleDelete = () => {
    // TODO: Implement delete functionality
    handleMenuClose();
  };

  const getVisitUrl = (subdomain) => {
    const currentUrl = window.location.origin;
    const urlParts = currentUrl.split('://');
    const protocol = urlParts[0];
    const domain = urlParts[1].split('.').slice(-2).join('.');
    
    // For development
    if (domain.includes('localhost')) {
      return `${protocol}://${subdomain}.localhost:3000`;
    }
    
    // For production
    return `${protocol}://${subdomain}.${domain}`;
  };

  const handleVisit = (subdomain) => {
    const siteUrl = getVisitUrl(subdomain);
    window.open(siteUrl, '_blank');
  };

  const handleSelectSite = (site) => {
    navigate(`/accounts/${accountId}/sites/${site.id}`);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
          Micro-Sites
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setIsCreateOpen(true)}
        >
          Create New Site
        </Button>
      </Box>

      <Grid container spacing={3}>
        {microSites?.map((site) => (
          <Grid item xs={12} md={6} lg={4} key={site.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography variant="h6" gutterBottom>
                    {site.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, site)}
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>

                <Typography color="textSecondary" gutterBottom>
                  {site.subdomain}.{site?.domain}
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <Chip
                    label={site.status}
                    size="small"
                    color={site.status === 'active' ? 'success' : 'default'}
                    sx={{ mr: 1 }}
                  />
                  <Chip
                    label={site.theme || 'default'}
                    size="small"
                  />
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<LaunchIcon />}
                  onClick={() => handleVisit(site.subdomain)}
                >
                  Visit Site
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEdit(site)}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  onClick={() => handleSelectSite(site)}
                >
                  Manage
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit Site
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete Site
        </MenuItem>
      </Menu>

      <CreateMicroSite
        open={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        accountId={accountId}
      />

      <EditMicroSite
        open={isEditOpen}
        onClose={handleEditClose}
        accountId={accountId}
        site={selectedSite}
      />
    </>
  );
}

export default MicroSiteList; 